// WaveTalk web client — the TRUSTED camera capture sheet (camera.capture). A
// mini-app's `sema.camera.capture()` is proxied from the worker to the main
// thread; this presents a host-owned camera UI (live preview + shutter + cancel),
// captures ONE still on the main thread, and returns only the encoded image. The
// mini-app never receives the MediaStream, never sees the preview, and can't keep
// the camera open — exactly the WeChat model (the host mediates the device, the
// app gets a result).
//
// Unlike the payment sheet, this is NOT rendered through the Joana catalog: a live
// camera preview is an imperative `<video>` bound to a MediaStream, which the
// declarative render layer doesn't (and shouldn't) model. This is TRUSTED
// first-party shell code, so owning a small piece of real DOM here is correct (the
// "never raw DOM" rule binds UNTRUSTED mini-apps, not the shell — ADR-0011).

import { hostTranslator } from "../joana/i18n/index.ts";

export interface CaptureOptions {
  /** "user" (front) or "environment" (rear). Default "environment". */
  facing?: "user" | "environment";
  /** Longest output edge in px; the still is downscaled to fit. Default 1280. */
  max_size?: number;
  /** JPEG quality 0–1. Default 0.85. */
  quality?: number;
}

export interface CaptureResult {
  /** A `data:image/jpeg;base64,…` URL of the captured still. */
  data_url: string;
  width: number;
  height: number;
}

/** The MediaDevices surface we use (injectable for tests). */
export interface MediaProvider {
  getUserMedia(constraints: { video: unknown }): Promise<MediaStreamLike>;
}
export interface MediaStreamLike {
  getTracks(): Array<{ stop(): void }>;
}

export class CameraCancelled extends Error {
  constructor() {
    super("camera cancelled");
    this.name = "CameraCancelled";
  }
}

export class CameraSheet {
  private readonly video: HTMLVideoElement;
  private readonly statusEl: HTMLElement;
  /** Labelled per capture, not per construction — ADR-0028. */
  private readonly cancelButton: HTMLElement;
  private readonly shutterButton: HTMLElement;
  private stream: MediaStreamLike | null = null;
  private pending: { resolve: (r: CaptureResult) => void; reject: (e: Error) => void } | null = null;

  constructor(
    private readonly layer: HTMLElement,
    private readonly media: MediaProvider = navigator.mediaDevices as unknown as MediaProvider,
  ) {
    layer.style.display = "none";
    const frame = document.createElement("div");
    frame.className = "cam-frame";
    this.video = document.createElement("video");
    this.video.className = "cam-preview";
    this.video.setAttribute("playsinline", "");
    this.video.muted = true;
    this.statusEl = document.createElement("div");
    this.statusEl.className = "cam-status";

    const controls = document.createElement("div");
    controls.className = "cam-controls";
    // Built once, but LABELLED at each capture (see `relabel`): the sheet is
    // constructed with the layer and never rebuilt, and translating here would
    // freeze these two words in whatever language the page loaded in. The
    // drive caught exactly that — a profile screen wholly in Kiswahili with an
    // English Cancel and Capture sitting on the camera behind it.
    this.cancelButton = button("", "cam-cancel", () => this.cancel());
    this.shutterButton = button("", "cam-shutter", () => void this.snap());
    controls.append(this.cancelButton, this.shutterButton);
    frame.append(this.video, this.statusEl, controls);
    layer.append(frame);
  }

  get isOpen(): boolean {
    return this.pending !== null;
  }

  /** Open the camera and resolve with the captured still, or reject with
   *  {@link CameraCancelled} / a device error. One capture at a time. */
  async capture(opts: CaptureOptions = {}): Promise<CaptureResult> {
    if (this.pending) this.cancel();
    this.layer.style.display = "flex";
    const t = hostTranslator();
    this.cancelButton.textContent = t("Cancel");
    this.shutterButton.textContent = t("◉ Capture");
    this.setStatus(t("Starting camera…"));
    const facing = opts.facing === "user" ? "user" : "environment";
    try {
      this.stream = await this.media.getUserMedia({ video: { facingMode: facing } });
    } catch (e) {
      this.hide();
      throw new Error(`camera unavailable: ${e instanceof Error ? e.message : String(e)}`);
    }
    this.video.srcObject = this.stream as unknown as MediaStream;
    try {
      await this.video.play();
    } catch {
      // autoplay can resolve late on some engines; the frame is still captured
    }
    this.setStatus("");
    this.maxSize = opts.max_size && opts.max_size > 0 ? opts.max_size : 1280;
    this.quality = opts.quality && opts.quality > 0 && opts.quality <= 1 ? opts.quality : 0.85;
    return new Promise<CaptureResult>((resolve, reject) => {
      this.pending = { resolve, reject };
    });
  }

  private maxSize = 1280;
  private quality = 0.85;

  private async snap(): Promise<void> {
    if (!this.pending) return;
    const vw = this.video.videoWidth || 640;
    const vh = this.video.videoHeight || 480;
    const scale = Math.min(1, this.maxSize / Math.max(vw, vh));
    const w = Math.max(1, Math.round(vw * scale));
    const h = Math.max(1, Math.round(vh * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      this.fail(new Error("camera: canvas unavailable"));
      return;
    }
    ctx.drawImage(this.video, 0, 0, w, h);
    const data_url = canvas.toDataURL("image/jpeg", this.quality);
    const pending = this.pending;
    this.pending = null;
    this.stop();
    this.hide();
    pending?.resolve({ data_url, width: w, height: h });
  }

  private cancel(): void {
    const pending = this.pending;
    this.pending = null;
    this.stop();
    this.hide();
    pending?.reject(new CameraCancelled());
  }

  private fail(err: Error): void {
    const pending = this.pending;
    this.pending = null;
    this.stop();
    this.hide();
    pending?.reject(err);
  }

  private stop(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.video.srcObject = null;
  }

  private hide(): void {
    this.layer.style.display = "none";
  }

  private setStatus(msg: string): void {
    this.statusEl.textContent = msg;
  }
}

function button(label: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement("button");
  b.className = cls;
  b.textContent = label;
  b.setAttribute("data-joana", "button"); // so the E2E can target it like catalog buttons
  b.addEventListener("click", onClick);
  return b;
}
